import { useEffect, useState } from 'react';
import { Folder, AlertCircle } from 'lucide-react';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { documentsApi, ApiError } from '@solara/api-client';
import type { DocumentDto } from '@solara/api-client';

const CATEGORIES = ['KYC', 'INVOICE_ARCHIVE', 'BANK_STATEMENT', 'CONTRACT', 'TAX_FILING', 'OTHER'] as const;

export default function DocumentsPage() {
  const { t } = useTranslation('p1');
  const [docs, setDocs] = useState<DocumentDto[]>([]);
  const [category, setCategory] = useState<string | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [category]);

  async function load() {
    try {
      const list = await documentsApi.list({ category: category === 'all' ? undefined : category });
      setDocs(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('documents.title')}</h1>
          <p className="text-fg-muted text-sm">{t('documents.subtitle')}</p>
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as string)}
          className="rounded border border-border bg-surface px-3 py-2 text-sm"
        >
          <option value="all">{t('common.all')}</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Folder className="h-4 w-4" />
            {t('documents.documentsCount', { count: docs.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {docs.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('documents.noInCategory')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {docs.map((doc) => (
                <li key={doc.id} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium">{doc.originalFilename}</p>
                    <p className="text-xs text-fg-muted">
                      {doc.category} · {t('documents.uploaded')}{' '}
                      {new Date(doc.uploadedAt).toLocaleString()}
                      {doc.expiryDate && (
                        <span className="ms-2">
                          {t('documents.expiresOn', {
                            date: new Date(doc.expiryDate).toLocaleDateString(),
                          })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone="neutral" className="text-[10px]">
                      {doc.bucketType}
                    </Badge>
                    <Badge
                      tone={
                        doc.virusScanStatus === 'CLEAN'
                          ? 'success'
                          : doc.virusScanStatus === 'INFECTED'
                          ? 'danger'
                          : 'neutral'
                      }
                      className="text-[10px]"
                    >
                      {t('documents.virusLabel')}:{' '}
                      {t(`documents.virusStatuses.${doc.virusScanStatus}`, {
                        defaultValue: doc.virusScanStatus.toLowerCase(),
                      })}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
